/**
 * MIL-STD-2525 military symbology icon generation.
 * Creates SVG strings and styled DOM elements for unit type markers.
 */

import { lucideSvgString } from './lucide-marker-icons';

/** Shape used for the affiliation frame */
type AffiliationShape = 'rectangle' | 'diamond' | 'circle';

/** Default icon size in pixels */
const DEFAULT_SIZE = 40;

/** Affiliation to frame shape mapping */
const AFFILIATION_SHAPES: ReadonlyMap<string, AffiliationShape> = new Map([
  ['f', 'rectangle'],
  ['h', 'diamond'],
  ['n', 'circle'],
  ['u', 'circle'],
]);

/** Affiliation to fill color mapping (vibrant, high-contrast palette) */
const AFFILIATION_FILLS: ReadonlyMap<string, string> = new Map([
  ['f', '#4fc3f7'],
  ['h', '#ef5350'],
  ['n', '#66bb6a'],
  ['u', '#fdd835'],
]);

/** Affiliation to glow/border color (brighter variant) */
const AFFILIATION_GLOW: ReadonlyMap<string, string> = new Map([
  ['f', '#29b6f6'],
  ['h', '#f44336'],
  ['n', '#43a047'],
  ['u', '#fbc02d'],
]);

/** Domain to symbol code mapping */
const DOMAIN_SYMBOLS: ReadonlyMap<string, string> = new Map([
  ['G', 'GND'],
  ['A', 'AIR'],
  ['S', 'SEA'],
  ['U', 'SUB'],
]);

/** Generate an SVG string for a CoT type with glow effect */
export function generateMarkerSvg(cotType: string, size: number = DEFAULT_SIZE): string {
  const affiliationChar = extractAffiliation(cotType);
  const shape = AFFILIATION_SHAPES.get(affiliationChar) ?? 'circle';
  const fill = AFFILIATION_FILLS.get(affiliationChar) ?? '#9e9e9e';
  const glow = AFFILIATION_GLOW.get(affiliationChar) ?? '#757575';
  const symbol = extractSymbolCode(cotType);

  return buildSvg(size, shape, fill, glow, symbol);
}

/** Get the glow color for a CoT type (used by track history and other visuals) */
export function getAffiliationColor(cotType: string): string {
  const affiliationChar = extractAffiliation(cotType);
  return AFFILIATION_GLOW.get(affiliationChar) ?? '#757575';
}

/**
 * Create a styled DOM element with the marker SVG and callsign label.
 * Used as AdvancedMarkerElement content in 2D view.
 */
export function createMarkerElement(
  cotType: string,
  callsign?: string,
  size: number = DEFAULT_SIZE,
  icon?: string,
  simulated: boolean = false,
): HTMLElement {
  const affiliationChar = extractAffiliation(cotType);
  const glow = AFFILIATION_GLOW.get(affiliationChar) ?? '#757575';
  const fill = AFFILIATION_FILLS.get(affiliationChar) ?? '#9e9e9e';
  const svg = lucideSvgString(icon, fill, size) ?? generateMarkerSvg(cotType, size);

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer';

  if (simulated) container.appendChild(buildSimBadge());

  // Icon wrapper with drop shadow
  const iconWrap = document.createElement('div');
  iconWrap.innerHTML = svg;
  iconWrap.style.cssText = [
    `width:${size}px`,
    `height:${size}px`,
    `filter:drop-shadow(0 0 6px ${glow}80) drop-shadow(0 2px 4px rgba(0,0,0,0.5))`,
    // Dashed ring unmistakably marks exercise/simulated tracks vs live traffic.
    simulated ? 'border:1.5px dashed #f4c43f;border-radius:50%;box-sizing:content-box;padding:1px' : '',
  ].filter(Boolean).join(';');
  container.appendChild(iconWrap);

  // Callsign label
  if (callsign) {
    const label = document.createElement('div');
    label.textContent = callsign;
    label.style.cssText = [
      'margin-top:2px',
      'padding:1px 5px',
      'background:rgba(0,0,0,0.75)',
      'color:#fff',
      'font-family:Inter,system-ui,sans-serif',
      'font-size:10px',
      'font-weight:600',
      'border-radius:3px',
      'white-space:nowrap',
      'max-width:100px',
      'overflow:hidden',
      'text-overflow:ellipsis',
      'text-align:center',
      `border:1px solid ${glow}60`,
    ].join(';');
    container.appendChild(label);
  }

  return container;
}

/** A small "SIM" chip marking an exercise/simulated track. */
function buildSimBadge(): HTMLElement {
  const badge = document.createElement('div');
  badge.textContent = 'SIM';
  badge.style.cssText = [
    'margin-bottom:2px',
    'padding:0 4px',
    'background:#f4c43f',
    'color:#000',
    'font-family:Inter,system-ui,sans-serif',
    'font-size:8px',
    'font-weight:800',
    'letter-spacing:0.05em',
    'border-radius:2px',
  ].join(';');
  return badge;
}

/** Extract the affiliation character from a CoT type */
function extractAffiliation(cotType: string): string {
  const dashIdx = cotType.indexOf('-');
  if (dashIdx === -1) return 'u';
  const secondDash = cotType.indexOf('-', dashIdx + 1);
  const end = secondDash === -1 ? cotType.length : secondDash;
  return cotType.slice(dashIdx + 1, end) || 'u';
}

/** Extract a short symbol code for the unit type */
function extractSymbolCode(cotType: string): string {
  const parts = cotType.split('-');
  const domain = parts[2] ?? '';
  return DOMAIN_SYMBOLS.get(domain) ?? '';
}

/** Build the SVG string with glow filter */
function buildSvg(
  size: number,
  shape: AffiliationShape,
  fill: string,
  glow: string,
  symbol: string,
): string {
  const half = size / 2;
  const pad = 5;

  const glowFilter = [
    '<defs>',
    '  <filter id="g" x="-40%" y="-40%" width="180%" height="180%">',
    `    <feDropShadow dx="0" dy="0" stdDeviation="2.5" flood-color="${glow}" flood-opacity="0.6"/>`,
    '  </filter>',
    '</defs>',
  ].join('');

  const frameShape = buildFrame(half, pad, shape, fill, glow);
  const symbolText = symbol
    ? `<text x="${half}" y="${half + 4}" text-anchor="middle" font-size="10" font-weight="700" font-family="Inter,system-ui,sans-serif" fill="#000" fill-opacity="0.85">${symbol}</text>`
    : '';

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`,
    glowFilter,
    `<g filter="url(#g)">`,
    frameShape,
    symbolText,
    '</g>',
    '</svg>',
  ].join('');
}

/** Build the affiliation frame shape SVG element */
function buildFrame(
  half: number,
  pad: number,
  shape: AffiliationShape,
  fill: string,
  stroke: string,
): string {
  const sw = 2;

  if (shape === 'rectangle') {
    return `<rect x="${pad}" y="${pad}" width="${(half - pad) * 2}" height="${(half - pad) * 2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" rx="3"/>`;
  }

  if (shape === 'diamond') {
    const pts = `${half},${pad} ${half * 2 - pad},${half} ${half},${half * 2 - pad} ${pad},${half}`;
    return `<polygon points="${pts}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`;
  }

  return `<circle cx="${half}" cy="${half}" r="${half - pad}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`;
}
